import { AUTH_LOGIN, AUTH_LOGOUT, AUTH_CHECK, AUTH_ERROR, AUTH_GET_PERMISSIONS } from 'react-admin';

import api from '~config/api';
import { UNAUTHORIZED } from '~constants/errors';
import { requiredHeaders } from '~constants/config';
import { userLogin } from '~services/AuthServices';
import { setAuthHeaders, checkAuthHeaders, storeUserData, clearUserData } from '~utils/helpers';
import {
  ACCESS_BACKOFFICE,
  clearPermissions,
  readPermissions,
  storePermissions
} from '~utils/permissions';

const clearSession = () => {
  requiredHeaders.forEach(header => localStorage.removeItem(header));
  clearUserData();
  clearPermissions();
};

const storeIdentity = data => {
  storeUserData({
    name: data.username,
    // Never write "null": it would render as the missing key `userType.null`.
    userType: data.role || ''
  });
  storePermissions(data);
};

// Whether this page load has already confirmed the cached identity with the server.
// Module state, so it resets on every full page load -- once per load, not per route.
let identityVerified = false;

const sameCodes = (a, b) => a.length === b.length && a.every(code => b.indexOf(code) !== -1);

/**
 * Confirm the cached capabilities with the server, once per page load.
 *
 * The cache in localStorage is only a snapshot from login time. When a role's
 * permissions change on the server (a `sync_roles`, a role reassigned), a
 * still-open session would otherwise keep showing buttons and routes the user no
 * longer has -- the server would refuse the action, but the UI would be lying.
 */
const verifyIdentity = () =>
  api
    .get('auth/me', {}, { headers: { Authorization: localStorage.getItem('Authorization') } })
    .then(response => {
      if (response.status === UNAUTHORIZED) {
        clearSession();
        return Promise.reject(Error());
      }
      if (!response.ok || !response.data) {
        // A transient failure (network, 5xx) must not log anyone out: keep the cached
        // capabilities. With nothing cached there is nothing to render with.
        if (readPermissions().codes.length) {
          return Promise.resolve();
        }
        clearSession();
        return Promise.reject(Error());
      }
      const fresh = response.data.permissions || [];
      if (!fresh.includes(ACCESS_BACKOFFICE)) {
        clearSession();
        return Promise.reject(Error());
      }
      const changed = !sameCodes(readPermissions().codes, fresh);
      storeIdentity(response.data);
      identityVerified = true;
      // <Admin> reads permissions once, at mount, so a change only takes effect on a
      // fresh mount. It cannot loop: after the reload the cache matches the server.
      if (changed) {
        window.location.reload();
      }
      return Promise.resolve();
    });

function authProvider(type, params) {
  if (type === AUTH_LOGIN) {
    const { username, password } = params;
    return userLogin({ username, password }).then(response => {
      if (!response.ok) {
        throw Error('auth.credentialsError');
      }
      const { data } = response;
      setAuthHeaders({
        'Authorization': 'Bearer ' + data.token,
      });
      // Identity comes from the server, not from an assumption: a read-only user
      // holds a valid token but must not get past the login screen.
      if (!(data.permissions || []).includes(ACCESS_BACKOFFICE)) {
        clearSession();
        throw Error('auth.noBackofficeAccess');
      }
      storeIdentity(data);
      // Just fetched from the server: nothing to re-verify on this page load.
      identityVerified = true;
      return Promise.resolve();
    });
  }
  if (type === AUTH_LOGOUT) {
    clearSession();
    identityVerified = false;
    return Promise.resolve();
  }
  if (type === AUTH_ERROR) {
    const { status } = params;
    // Only 401 ends the session. A 403 is now an ordinary outcome -- a country
    // manager saving another country's project -- and must not log them out.
    if (status === UNAUTHORIZED) {
      clearSession();
      return Promise.reject(Error());
    }
    return Promise.resolve();
  }
  if (type === AUTH_CHECK) {
    if (!checkAuthHeaders()) {
      return Promise.reject(Error());
    }
    return identityVerified ? Promise.resolve() : verifyIdentity();
  }
  if (type === AUTH_GET_PERMISSIONS) {
    // Synchronous/local on purpose: v2 calls this on every WithPermissions mount.
    return Promise.resolve(readPermissions());
  }
  return Promise.resolve();
}

export default authProvider;
